import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useContext } from 'react';

import { useAnalytics } from '~/analytics';
import { type CsvColumn, exportRowsAsCsv } from '~/utils/exportRows';

import { ChatAttributionContext } from './ChatComponents';

/**
 * Export affordance for a chat report widget.
 *
 * Reads from data already in the browser, so there is no request, no loading
 * state and nothing to revalidate — a download is not a mutation.
 */
export function ExportCsvButton<TRow>({
  rows,
  getRows,
  columns,
  getColumns,
  title,
  blockType,
}: {
  /** Rows known at render time. Omit when they can only be read on click. */
  rows?: TRow[];
  /**
   * Lazy alternative to `rows`, resolved on click. Markdown tables need this:
   * their rows only exist in the DOM (Streamdown renders the cells through its
   * own components, so they aren't reachable as children), and reading a ref
   * during render would mean measuring in an effect and holding the result in
   * state — a cascading-render smell for a value only needed on click.
   */
  getRows?: () => TRow[];
  columns?: CsvColumn<TRow>[];
  getColumns?: (rows: TRow[]) => CsvColumn<TRow>[];
  title: string;
  /** Analytics discriminator; matches the ContentBlock `type`. */
  blockType: string;
}) {
  const { track } = useAnalytics();
  const { sessionId } = useContext(ChatAttributionContext);

  // With eager rows we can hide the affordance when there's nothing to write —
  // an empty file is a worse answer than no button. With `getRows` the count
  // isn't known until click, so the button renders and the click no-ops.
  if (!getRows && (rows?.length ?? 0) === 0) return null;

  return (
    <Tooltip title="Download as CSV">
      <Box
        component="button"
        type="button"
        aria-label={`Download ${title} as CSV`}
        onClick={() => {
          const resolvedRows = getRows ? getRows() : (rows ?? []);
          if (resolvedRows.length === 0) return;
          const resolvedColumns = getColumns ? getColumns(resolvedRows) : (columns ?? []);
          if (resolvedColumns.length === 0) return;

          exportRowsAsCsv(resolvedRows, resolvedColumns, title);
          track('chat_export_csv', {
            session_id: sessionId,
            block_type: blockType,
            row_count: resolvedRows.length,
          });
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          borderRadius: 1,
          px: 0.75,
          py: 0.25,
          color: 'text.secondary',
          transition: 'all 0.15s',
          '&:hover': {
            background: alpha('#000', 0.04),
            color: 'text.primary',
          },
        }}
      >
        <FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />
        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
          CSV
        </Typography>
      </Box>
    </Tooltip>
  );
}
